import type { Container, Sprite } from 'pixi.js';
import { makeSprite } from '@/assets/resolver';
import type { BossDef } from './boss-base';
import { setCircle, type HitCircle } from './boss-hooks';

/**
 * C7 Kraken — def, tuning and arm scaffolding (class logic in kraken.ts).
 * docs/02-bosses.md + docs/12 C7.
 */
export const KRAKEN: BossDef = {
  id: 'kraken',
  nameKey: 'boss.kraken',
  tier: 3,
  minRoom: 3,
  baseHp: 1500,
  radius: 56,
  bodyKey: 'boss/kraken/body',
  movement: 'orbit', // unused — fully custom movement
  contactDamage: 1,
  phases: [{ hpPct: 1.0, patterns: [] }],
  evolve: [
    {
      // Ink spit: the mantle bulges, then a fan of heavy ink drops
      tier: 2,
      pattern: {
        type: 'aimed', count: 3, spreadDeg: 24, interval: 3.6,
        telegraph: 0.5,
        bullet: 'bullet/enemy/ring', speed: 230, radius: 10, damage: 2,
      },
    },
    {
      // Undertow: a slow ink pinwheel from the mantle
      tier: 3,
      pattern: {
        type: 'spiral', arms: 2, interval: 0.34, angularSpeed: -45,
        bullet: 'bullet/enemy/ball', speed: 125, radius: 10, damage: 1,
      },
    },
  ],
};

/** All Kraken tuning in one place. */
export const KR = {
  armCount: 8,
  armSegs: 5,
  segDist: 29,
  armBaseR: 64,
  segRadius: 14,
  tipRadius: 18,
  waveAmp: 0.45,
  waveSpeed: 1.25,
  /** Mantle's share of max HP — the rest splits across the arms */
  bodyShare: 0.4,
  /** Lash cadence at tempo 1 — divided by the tier tempo */
  lashInterval: 5.0,
  lashWindup: 0.6,
  lashTime: 0.85,
  /** Segment spacing multiplier at full lash reach */
  lashExtend: 2.1,
  tipFireInterval: 4.2,
  ringInterval: 5.0,
  ringCount: 8,
  stealRatio: 1.0,
  /** Phase-1 drift around home (rad/s) */
  driftSpeed: 0.22,
  /** Enraged mantle chase (px/s) */
  chaseSpeed: 95,
  spiralInterval: 0.16,
  spiralDegPerShot: 23,
} as const;

export interface Arm {
  alive: boolean;
  hp: number;
  maxHp: number;
  baseAngle: number;
  /** Current reach multiplier (1 = rest, lashExtend = full lash) */
  extend: number;
  windup: number;
  lashing: number;
  /** Aim angle locked when the windup ends */
  lashAngle: number;
  segs: Sprite[];
  tip: Sprite;
  /** World-space tip position (collision + bullets) */
  tipX: number;
  tipY: number;
}

/** Build the 8 arm sprite chains, beads under the mantle (HP set by the boss). */
export function buildArms(view: Container, x: number, y: number): Arm[] {
  const arms: Arm[] = [];
  for (let i = 0; i < KR.armCount; i++) {
    const segs: Sprite[] = [];
    for (let s = 0; s < KR.armSegs; s++) {
      const bead = makeSprite('boss/kraken/segment');
      bead.scale.set(1 - s * 0.09);
      segs.push(bead);
      view.addChildAt(bead, 0);
    }
    const tip = makeSprite('boss/kraken/tip');
    view.addChildAt(tip, 0);
    arms.push({
      alive: true, hp: 0, maxHp: 0,
      baseAngle: (Math.PI * 2 * i) / KR.armCount,
      extend: 1, windup: 0, lashing: 0, lashAngle: 0,
      segs, tip, tipX: x, tipY: y,
    });
  }
  return arms;
}

/** Bead position along the straight chord from the mantle to the tip. */
export function segWorldPos(
  cx: number, cy: number, arm: Arm, index: number,
): { x: number; y: number } {
  const t = (index + 1) / (KR.armSegs + 1);
  return { x: cx + (arm.tipX - cx) * t, y: cy + (arm.tipY - cy) * t };
}

/**
 * Nearest living arm part (tip or bead) inside a blast circle (X5).
 * `at` receives that part's hit circle (the explosion number anchors there).
 */
export function armInBlast(
  arms: ReadonlyArray<Arm>, cx: number, cy: number, x: number, y: number, radius: number,
  at?: HitCircle,
): Arm | null {
  let best: Arm | null = null;
  let bestD = Infinity;
  for (const arm of arms) {
    if (!arm.alive) continue;
    const d = Math.hypot(arm.tipX - x, arm.tipY - y) - KR.tipRadius;
    if (d < radius && d < bestD) {
      bestD = d;
      best = arm;
      if (at) setCircle(at, arm.tipX, arm.tipY, KR.tipRadius);
    }
    for (let s = 0; s < KR.armSegs; s++) {
      const p = segWorldPos(cx, cy, arm, s);
      const ds = Math.hypot(p.x - x, p.y - y) - KR.segRadius;
      if (ds < radius && ds < bestD) {
        bestD = ds;
        best = arm;
        if (at) setCircle(at, p.x, p.y, KR.segRadius);
      }
    }
  }
  return best;
}
